import React, { useState, useEffect, useMemo } from 'react'
import {
  GetUserProfileOutput,
  getUserProfile,
  updateUserNickname,
  updateUserRegion,
  updateUserDemographics,
  changeUserPassword,
} from '../sdk/user'
import { useToken } from '../hooks/useToken'
import { Link, useNavigate } from 'react-router-dom'
import { Locale, translate, useLang } from '../components/Locale'
import LanguageOption from '../components/LanguageOption'
import './Profile.css'
import { usePageLayout } from '../hooks/usePageLayout'
import { useGet } from '../hooks/useGet'
import { getRegions } from '../sdk/stats'
import { Demographic } from '../components/Demographic'
import {
  prevent_auto_complete_as_password,
  validatePassword,
} from '../utils/password'
import { showToast } from '../utils/alert'

function Profile() {
  // Memoize layout options to prevent infinite re-renders
  const layoutOptions = useMemo(
    () => ({
      showSidebar: true,
      showHeader: true,
      showFooter: true,
      title: {
        en: 'Profile',
        zh_cn: '个人资料',
        zh_hk: '個人資料',
      },
    }),
    [],
  )

  // Profile page should show all navigation elements
  usePageLayout(layoutOptions)

  const { token, logout } = useToken()

  const { lang } = useLang()

  const [profile, setProfile] = useState<GetUserProfileOutput | null>(null)

  const [newPassword1, setNewPassword1] = useState<string | null>(null)
  const [newPassword2, setNewPassword2] = useState<string | null>(null)

  const passwordValidateError = newPassword1
    ? validatePassword(lang, newPassword1)
    : null
  const passwordError =
    passwordValidateError ||
    (newPassword1 && newPassword2 && newPassword1 !== newPassword2
      ? translate(lang, {
          en: 'Passwords do not match',
          zh_cn: '密码不匹配',
          zh_hk: '密碼不匹配',
        })
      : null)

  const [saveNicknameError, setSaveNicknameError] = useState<string | null>(
    null,
  )
  const [saveRegionError, setSaveRegionError] = useState<string | null>(null)
  const [savePasswordError, setSavePasswordError] = useState<string | null>(
    null,
  )

  const navigate = useNavigate()

  const regions = useGet({ regions: [] }, () => getRegions({}))

  useEffect(() => {
    if (token) {
      getUserProfile({}).then(setProfile)
    }
  }, [token])

  useEffect(() => {
    if (!token) {
      navigate('/login')
    }
  }, [token])

  function setRegion(value: string) {
    if (!profile) return
    setProfile(profile =>
      profile ? { ...profile, region: value || null } : null,
    )
    updateUserRegion({
      params: {
        id: profile.user_id,
      },
      body: {
        region: value,
      },
    })
      .then(() => setSaveRegionError(null))
      .catch(error => setSaveRegionError(String(error)))
  }

  function setNickname(value: string) {
    if (!profile) return
    setProfile(profile =>
      profile ? { ...profile, nickname: value || null } : null,
    )
    updateUserNickname({
      params: {
        id: profile.user_id,
      },
      body: {
        nickname: value,
      },
    })
      .then(() => setSaveNicknameError(null))
      .catch(error => setSaveNicknameError(String(error)))
  }

  async function submitNewPassword() {
    if (!newPassword1) return
    try {
      await changeUserPassword({
        body: {
          new_password: newPassword1,
        },
      })
    } catch (error) {
      setSavePasswordError(String(error))
      return
    }
    setSavePasswordError(null)
    setNewPassword1(null)
    setNewPassword2(null)
    showToast({
      icon: 'success',
      title: translate(lang, {
        en: 'New password is effective now',
        zh_cn: '新密码已生效',
        zh_hk: '新密碼已生效',
      }),
    })
  }

  function renderBody() {
    if (!token) {
      return (
        <>
          <p>
            <Locale
              en="This page is only for logged in users."
              zh_cn="此页面仅限已登录用户访问。"
              zh_hk="此頁面僅限已登入用戶訪問。"
            />
          </p>
          <Link to="/login">
            <button>
              <Locale
                en="Login / Register now"
                zh_cn="立即登录 / 注册"
                zh_hk="立即登入 / 註冊"
              />
            </button>
          </Link>
        </>
      )
    }
    if (!profile) {
      return (
        <>
          <Locale en="Loading..." zh_cn="加载中..." zh_hk="加載中..." />
        </>
      )
    }
    return (
      <>
        <div className="profile-header">
          <div className="greeting">
            <Locale en="Welcome" zh_cn="欢迎" zh_hk="歡迎" />,{' '}
            <span className="username">
              {profile.username || profile.email}
            </span>
          </div>
          <nav>
            <Link to="/top-up">
              <button className="primary-button">
                <Locale en="Top Up" zh_cn="充值" zh_hk="儲值" />
              </button>
            </Link>
            <Link to="/history">
              <button className="primary-button">
                <Locale en="My Scenes" zh_cn="我的场景" zh_hk="我的場景" />
              </button>
            </Link>
            <button className="primary-button" onClick={logout}>
              <Locale en="Logout" zh_cn="登出" zh_hk="登出" />
            </button>
          </nav>
        </div>

        <div className="layout">
          <div className="section card profile-user">
            <div className="section-title">
              <Locale en="User Profile" zh_cn="用户资料" zh_hk="用戶資料" />
            </div>
            <div className="section-content user-info">
              <div className="demographic-field">
                <Locale en="Nickname" zh_cn="稱呼" zh_hk="称呼" />:{' '}
                {prevent_auto_complete_as_password}
                <input
                  value={profile.nickname || ''}
                  autoComplete="name"
                  onChange={e => setNickname(e.target.value)}
                  placeholder={profile.username || profile.email?.split('@')[0]}
                />
                {!!saveNicknameError && (
                  <div className="error">{saveNicknameError}</div>
                )}
              </div>
              <div className="">
                <Locale en="Email" zh_cn="电邮" zh_hk="電郵" />: {profile.email}
              </div>
              <div className="demographic-field">
                <Locale en="Region" zh_cn="地区" zh_hk="地區" />:{' '}
                {prevent_auto_complete_as_password}
                <input
                  name="region"
                  list="regionList"
                  value={profile.region || ''}
                  onChange={e => setRegion(e.target.value)}
                />
                {!!saveRegionError && (
                  <div className="error">{saveRegionError}</div>
                )}
                <datalist id="regionList">
                  {regions.state.regions?.map(region => {
                    let names = Array.from(
                      new Set([region.name, ...region.native_names]),
                    )
                    return (
                      <option key={region.id}>
                        {region.flag} {names.join(' | ')}
                      </option>
                    )
                  })}
                </datalist>
              </div>
              <div className="demographic-field">
                <Locale en="New Password" zh_cn="换新密码" zh_hk="改新密碼" />:{' '}
                <input
                  type="password"
                  name="newPassword1"
                  autoComplete="new-password"
                  value={newPassword1 || ''}
                  onChange={e => setNewPassword1(e.target.value)}
                />
              </div>
              <div
                className="demographic-field"
                hidden={!newPassword1 || !!passwordValidateError}
              >
                <Locale
                  en="Confirm New Password"
                  zh_cn="确认密码"
                  zh_hk="確認密碼"
                />
                :{' '}
                <input
                  type="password"
                  name="newPassword2"
                  autoComplete="new-password"
                  value={newPassword2 || ''}
                  onChange={e => setNewPassword2(e.target.value)}
                />
              </div>
              <div className="demographic-field">
                {passwordError ? (
                  <p className="error" style={{ marginBottom: '0.5rem' }}>
                    {passwordError}
                  </p>
                ) : null}
                <button
                  className="primary-button"
                  disabled={!newPassword1 || !newPassword2 || !!passwordError}
                  onClick={submitNewPassword}
                  hidden={!newPassword2}
                >
                  <Locale
                    en="Submit New Password"
                    zh_cn="提交新密码"
                    zh_hk="提交新密碼"
                  />
                </button>
                {savePasswordError ? (
                  <div className="error">{savePasswordError}</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="section card profile-stats">
            <div className="section-title">
              <Locale en="Statistics" zh_cn="统计" zh_hk="統計" />
            </div>
            <div className="section-content stats">
              <div className="">
                <Locale en="Generated" zh_cn="已生成场景" zh_hk="已生成場景" />:{' '}
                {profile.scene_generated_count}{' '}
                <Locale en="" zh_cn="个" zh_hk="個" />
              </div>
              <div className="">
                <Locale en="Shared" zh_cn="已分享场景" zh_hk="已分享場景" />:{' '}
                {profile.scene_shared_count}{' '}
                <Locale en="" zh_cn="个" zh_hk="個" />
              </div>
              <div className="">
                <Locale en="Bookmarked" zh_cn="已收藏场景" zh_hk="已收藏場景" />
                : {profile.scene_bookmarked_count}{' '}
                <Locale en="" zh_cn="个" zh_hk="個" />
              </div>
              <div className="">
                <Locale en="Downloaded" zh_cn="已下载场景" zh_hk="已下載場景" />
                : {profile.scene_downloaded_count}{' '}
                <Locale en="" zh_cn="个" zh_hk="個" />
              </div>
            </div>
          </div>

          <div className="section card profile-demographics demographic">
            <div className="section-title">
              <Locale en="Demographics" zh_cn="人口统计" zh_hk="人口統計" />
            </div>
            <div className="section-content demographic-fields">
              <Demographic
                regions={regions.state.regions}
                profile={profile}
                setProfile={setProfile}
              />
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="profile-page">
        <div className="body-content">{renderBody()}</div>
      </div>
    </>
  )
}

export default Profile
